import React, { useMemo, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignLeft,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  BringToFront,
  Check,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
  FlipHorizontal2,
  FlipVertical2,
  Layers3,
  Lock,
  Mic,
  Pause,
  Play,
  RotateCcw,
  Save,
  SendToBack,
  SquareStack,
  Trash2,
  Unlock,
  Volume2,
} from 'lucide-react';
import type { AspectRatio, StoryElement } from '../types';
import { useEditorStore } from '../store/editor';
import { blobToDataUrl } from '../utils/export';
import { saveRevision } from '../storage/db';

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) => (
  <button type="button" className={`toggle ${checked ? 'is-on' : ''}`} onClick={onChange} role="switch" aria-checked={checked} aria-label={label}><span /></button>
);

const NumberField = ({ label, value, min, max, step = 1, onChange }: { label: string; value: number; min?: number; max?: number; step?: number; onChange: (value: number) => void }) => (
  <label className="field compact-field">
    <span>{label}</span>
    <input
      type="number"
      value={Number.isFinite(value) ? Math.round(value * 10) / 10 : 0}
      min={min}
      max={max}
      step={step}
      onChange={(event) => {
        const next = Number(event.target.value);
        if (Number.isFinite(next)) onChange(next);
      }}
    />
  </label>
);

export const Inspector: React.FC = () => {
  const project = useEditorStore((state) => state.project);
  const activeCellId = useEditorStore((state) => state.activeCellId);
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const setSelectedIds = useEditorStore((state) => state.setSelectedIds);
  const mutate = useEditorStore((state) => state.mutate);
  const deleteSelected = useEditorStore((state) => state.deleteSelected);
  const duplicateSelected = useEditorStore((state) => state.duplicateSelected);
  const moveLayer = useEditorStore((state) => state.moveLayer);
  const setRevisionsOpen = useEditorStore((state) => state.setRevisionsOpen);
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState('');
  const [revisionSaved, setRevisionSaved] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const activeCell = project?.cells.find((cell) => cell.id === activeCellId) ?? project?.cells[0];
  const selectedElements = useMemo(() => activeCell?.elements.filter((element) => selectedIds.includes(element.id)) ?? [], [activeCell?.elements, selectedIds]);
  const selected = selectedElements.length === 1 ? selectedElements[0] : null;
  const layers = useMemo(() => [...(activeCell?.elements ?? [])].sort((a, b) => b.zIndex - a.zIndex), [activeCell?.elements]);

  const updateCell = (updater: (cell: NonNullable<typeof activeCell>) => void) => {
    if (!activeCell) return;
    mutate((draft) => {
      const cell = draft.cells.find((item) => item.id === activeCell.id);
      if (cell) updater(cell);
    });
  };

  const updateElement = (id: string, updater: (element: StoryElement) => void) => {
    if (!activeCell) return;
    mutate((draft) => {
      const cell = draft.cells.find((item) => item.id === activeCell.id);
      const element = cell?.elements.find((item) => item.id === id);
      if (element) updater(element);
    });
  };

  const alignSelection = (mode: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom' | 'distribute-x' | 'distribute-y') => {
    if (!activeCell || selectedElements.length < 2) return;
    const bounds = selectedElements.reduce((result, element) => ({
      minX: Math.min(result.minX, element.x),
      minY: Math.min(result.minY, element.y),
      maxX: Math.max(result.maxX, element.x + element.width),
      maxY: Math.max(result.maxY, element.y + element.height),
    }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
    mutate((draft) => {
      const cell = draft.cells.find((item) => item.id === activeCell.id);
      const items = cell?.elements.filter((element) => selectedIds.includes(element.id)) ?? [];
      if (mode === 'distribute-x') {
        const sorted = [...items].sort((a, b) => a.x - b.x);
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const firstCenter = first.x + first.width / 2;
        const lastCenter = last.x + last.width / 2;
        sorted.forEach((element, index) => {
          if (index === 0 || index === sorted.length - 1) return;
          const center = firstCenter + ((lastCenter - firstCenter) * index) / (sorted.length - 1);
          element.x = center - element.width / 2;
        });
        return;
      }
      if (mode === 'distribute-y') {
        const sorted = [...items].sort((a, b) => a.y - b.y);
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const firstCenter = first.y + first.height / 2;
        const lastCenter = last.y + last.height / 2;
        sorted.forEach((element, index) => {
          if (index === 0 || index === sorted.length - 1) return;
          const center = firstCenter + ((lastCenter - firstCenter) * index) / (sorted.length - 1);
          element.y = center - element.height / 2;
        });
        return;
      }
      items.forEach((element) => {
        if (mode === 'left') element.x = bounds.minX;
        if (mode === 'center') element.x = (bounds.minX + bounds.maxX) / 2 - element.width / 2;
        if (mode === 'right') element.x = bounds.maxX - element.width;
        if (mode === 'top') element.y = bounds.minY;
        if (mode === 'middle') element.y = (bounds.minY + bounds.maxY) / 2 - element.height / 2;
        if (mode === 'bottom') element.y = bounds.maxY - element.height;
      });
    });
  };

  const toggleRecord = async () => {
    setRecordError('');
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setRecordError('Audio recording is not supported by this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => { if (event.data.size) chunksRef.current.push(event.data); };
      recorder.onstop = async () => {
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (blob.size > 0) {
          const dataUrl = await blobToDataUrl(blob);
          updateCell((cell) => { cell.audioDataUrl = dataUrl; });
        }
      };
      recorder.start();
      setRecording(true);
    } catch (error) {
      setRecordError(error instanceof Error ? error.message : 'Microphone permission was not granted.');
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  if (!project || !activeCell) return <aside className="inspector"><div className="empty-inspector">Loading inspector…</div></aside>;

  return (
    <aside className="inspector" aria-label="Inspector">
      <header className="inspector-header">
        <span className="eyebrow">Inspector</span>
        <h2>{selected ? selected.name : selectedElements.length > 1 ? `${selectedElements.length} objects` : activeCell.title || 'Scene'}</h2>
      </header>

      <div className="inspector-scroll">
        {selectedElements.length > 1 && (
          <section className="inspector-section">
            <h3>Align selection</h3>
            <div className="icon-grid alignment-buttons">
              <button type="button" className="icon-button" onClick={() => alignSelection('left')} title="Align left"><AlignLeft size={18} /></button>
              <button type="button" className="icon-button" onClick={() => alignSelection('center')} title="Align horizontal centers"><AlignCenter size={18} /></button>
              <button type="button" className="icon-button" onClick={() => alignSelection('right')} title="Align right"><AlignEndHorizontal size={18} /></button>
              <button type="button" className="icon-button" onClick={() => alignSelection('top')} title="Align top"><AlignStartVertical size={18} /></button>
              <button type="button" className="icon-button" onClick={() => alignSelection('middle')} title="Align vertical centers"><AlignVerticalDistributeCenter size={18} /></button>
              <button type="button" className="icon-button" onClick={() => alignSelection('bottom')} title="Align bottom"><AlignEndVertical size={18} /></button>
              <button type="button" className="icon-button" onClick={() => alignSelection('distribute-x')} title="Distribute horizontally"><AlignHorizontalDistributeCenter size={18} /></button>
              <button type="button" className="icon-button" onClick={() => alignSelection('distribute-y')} title="Distribute vertically"><AlignVerticalDistributeCenter size={18} /></button>
            </div>
            <div className="button-row">
              <button type="button" className="small-button" onClick={duplicateSelected}><Copy size={16} /> Duplicate</button>
              <button type="button" className="small-button danger" onClick={deleteSelected}><Trash2 size={16} /> Delete</button>
            </div>
          </section>
        )}

        {selected && (
          <>
            <section className="inspector-section">
              <div className="section-title-row"><h3>Object</h3><span className="type-pill">{selected.kind}</span></div>
              <label className="field"><span>Name</span><input value={selected.name} onChange={(event) => updateElement(selected.id, (element) => { element.name = event.target.value; })} /></label>
              <div className="four-column-fields">
                <NumberField label="X" value={selected.x} onChange={(value) => updateElement(selected.id, (element) => { element.x = value; })} />
                <NumberField label="Y" value={selected.y} onChange={(value) => updateElement(selected.id, (element) => { element.y = value; })} />
                <NumberField label="W" value={selected.width} min={20} onChange={(value) => updateElement(selected.id, (element) => { element.width = Math.max(20, value); })} />
                <NumberField label="H" value={selected.height} min={20} onChange={(value) => updateElement(selected.id, (element) => { element.height = Math.max(20, value); })} />
              </div>
              <div className="two-column-fields">
                <NumberField label="Rotation" value={selected.rotation} step={1} onChange={(value) => updateElement(selected.id, (element) => { element.rotation = value; })} />
                <NumberField label="Opacity %" value={selected.opacity * 100} min={0} max={100} onChange={(value) => updateElement(selected.id, (element) => { element.opacity = clamp(value / 100, 0, 1); })} />
              </div>
              <div className="icon-grid object-buttons">
                <button type="button" className={`icon-button ${selected.flipX ? 'is-active' : ''}`} onClick={() => updateElement(selected.id, (element) => { element.flipX = !element.flipX; })} title="Flip horizontally"><FlipHorizontal2 size={18} /></button>
                <button type="button" className={`icon-button ${selected.flipY ? 'is-active' : ''}`} onClick={() => updateElement(selected.id, (element) => { element.flipY = !element.flipY; })} title="Flip vertically"><FlipVertical2 size={18} /></button>
                <button type="button" className={`icon-button ${selected.locked ? 'is-active' : ''}`} onClick={() => updateElement(selected.id, (element) => { element.locked = !element.locked; })} title={selected.locked ? 'Unlock' : 'Lock'}>{selected.locked ? <Unlock size={18} /> : <Lock size={18} />}</button>
                <button type="button" className={`icon-button ${selected.hidden ? 'is-active' : ''}`} onClick={() => updateElement(selected.id, (element) => { element.hidden = !element.hidden; })} title={selected.hidden ? 'Show' : 'Hide'}>{selected.hidden ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                <button type="button" className="icon-button" onClick={() => moveLayer(selected.id, 'front')} title="Bring to front"><BringToFront size={18} /></button>
                <button type="button" className="icon-button" onClick={() => moveLayer(selected.id, 'back')} title="Send to back"><SendToBack size={18} /></button>
              </div>
              <div className="button-row">
                <button type="button" className="small-button" onClick={duplicateSelected}><Copy size={16} /> Duplicate</button>
                <button type="button" className="small-button danger" onClick={deleteSelected}><Trash2 size={16} /> Delete</button>
              </div>
            </section>

            {(selected.kind === 'text' || selected.kind === 'bubble') && selected.textStyle && (
              <section className="inspector-section">
                <h3>{selected.kind === 'bubble' ? 'Bubble text' : 'Typography'}</h3>
                <label className="field"><span>Text</span><textarea rows={4} value={selected.text ?? ''} onChange={(event) => updateElement(selected.id, (element) => { element.text = event.target.value; })} /></label>
                <div className="two-column-fields">
                  <label className="field compact-field"><span>Font</span><select value={selected.textStyle.fontFamily} onChange={(event) => updateElement(selected.id, (element) => { element.textStyle!.fontFamily = event.target.value; })}><option value="Inter, ui-sans-serif, system-ui, sans-serif">Modern sans</option><option value="Georgia, serif">Story serif</option><option value="'Trebuchet MS', sans-serif">Friendly</option><option value="'Courier New', monospace">Monospace</option></select></label>
                  <NumberField label="Size" value={selected.textStyle.fontSize} min={10} max={220} onChange={(value) => updateElement(selected.id, (element) => { element.textStyle!.fontSize = value; })} />
                </div>
                <div className="two-column-fields">
                  <label className="field compact-field"><span>Weight</span><select value={selected.textStyle.fontWeight} onChange={(event) => updateElement(selected.id, (element) => { element.textStyle!.fontWeight = Number(event.target.value); })}><option value={400}>Regular</option><option value={550}>Medium</option><option value={700}>Bold</option><option value={800}>Extra bold</option></select></label>
                  <label className="field compact-field"><span>Align</span><select value={selected.textStyle.align} onChange={(event) => updateElement(selected.id, (element) => { element.textStyle!.align = event.target.value as 'left' | 'center' | 'right'; })}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select></label>
                </div>
                <div className="colour-row">
                  <label><span>Text</span><input type="color" value={selected.textStyle.fill} onChange={(event) => updateElement(selected.id, (element) => { element.textStyle!.fill = event.target.value; })} /></label>
                  <label><span>Fill</span><input type="color" value={selected.textStyle.background === 'transparent' ? '#ffffff' : selected.textStyle.background} onChange={(event) => updateElement(selected.id, (element) => { element.textStyle!.background = event.target.value; })} /></label>
                  <label><span>Border</span><input type="color" value={selected.textStyle.border === 'transparent' ? '#27243c' : selected.textStyle.border} onChange={(event) => updateElement(selected.id, (element) => { element.textStyle!.border = event.target.value; })} /></label>
                </div>
                <div className="switch-row"><div><strong>Italic</strong></div><Toggle checked={selected.textStyle.italic} onChange={() => updateElement(selected.id, (element) => { element.textStyle!.italic = !element.textStyle!.italic; })} label="Toggle italic" /></div>
                <div className="switch-row"><div><strong>Underline</strong></div><Toggle checked={selected.textStyle.underline} onChange={() => updateElement(selected.id, (element) => { element.textStyle!.underline = !element.textStyle!.underline; })} label="Toggle underline" /></div>
              </section>
            )}

            {selected.kind === 'character' && selected.character && (
              <section className="inspector-section">
                <h3>Character studio</h3>
                <div className="two-column-fields">
                  <label className="field compact-field"><span>Expression</span><select value={selected.character.expression} onChange={(event) => updateElement(selected.id, (element) => { element.character!.expression = event.target.value as typeof selected.character.expression; })}><option value="happy">Happy</option><option value="neutral">Neutral</option><option value="sad">Sad</option><option value="angry">Angry</option><option value="surprised">Surprised</option></select></label>
                  <label className="field compact-field"><span>Pose</span><select value={selected.character.pose} onChange={(event) => updateElement(selected.id, (element) => { element.character!.pose = event.target.value as typeof selected.character.pose; })}><option value="stand">Stand</option><option value="wave">Wave</option><option value="point">Point</option><option value="celebrate">Celebrate</option><option value="sit">Sit</option></select></label>
                </div>
                <label className="field"><span>View</span><select value={selected.character.view} onChange={(event) => updateElement(selected.id, (element) => { element.character!.view = event.target.value as 'front' | 'side'; })}><option value="front">Front</option><option value="side">Side</option></select></label>
                <div className="colour-row character-colours">
                  {(['skin', 'hair', 'top', 'bottom', 'shoes'] as const).map((key) => (
                    <label key={key}><span>{key[0].toUpperCase() + key.slice(1)}</span><input type="color" value={selected.character![key]} onChange={(event) => updateElement(selected.id, (element) => { element.character![key] = event.target.value; })} /></label>
                  ))}
                </div>
              </section>
            )}

            {selected.kind === 'shape' && selected.appearance && (
              <section className="inspector-section">
                <h3>Shape style</h3>
                <label className="field"><span>Shape</span><select value={selected.shapeStyle} onChange={(event) => updateElement(selected.id, (element) => { element.shapeStyle = event.target.value as typeof selected.shapeStyle; })}><option value="rectangle">Rectangle</option><option value="ellipse">Ellipse</option><option value="star">Star</option><option value="arrow">Arrow</option><option value="line">Line</option><option value="heart">Heart</option></select></label>
                <div className="colour-row"><label><span>Fill</span><input type="color" value={selected.appearance.fill === 'transparent' ? '#ffffff' : selected.appearance.fill} onChange={(event) => updateElement(selected.id, (element) => { element.appearance!.fill = event.target.value; })} /></label><label><span>Stroke</span><input type="color" value={selected.appearance.stroke} onChange={(event) => updateElement(selected.id, (element) => { element.appearance!.stroke = event.target.value; })} /></label></div>
                <NumberField label="Stroke width" value={selected.appearance.strokeWidth} min={0} max={80} onChange={(value) => updateElement(selected.id, (element) => { element.appearance!.strokeWidth = value; })} />
              </section>
            )}

            {selected.kind === 'image' && (
              <section className="inspector-section">
                <h3>Image</h3>
                <label className="field"><span>Alternative text</span><textarea rows={3} value={selected.alt ?? ''} onChange={(event) => updateElement(selected.id, (element) => { element.alt = event.target.value; })} placeholder="Describe this image for accessibility" /></label>
                <button type="button" className="secondary-button full-width" onClick={() => updateElement(selected.id, (element) => { element.rotation = 0; element.flipX = false; element.flipY = false; element.opacity = 1; })}><RotateCcw size={17} /> Reset appearance</button>
              </section>
            )}
          </>
        )}

        {selectedElements.length === 0 && (
          <>
            <section className="inspector-section">
              <h3>Cell details</h3>
              <label className="field"><span>Title</span><input value={activeCell.title} onChange={(event) => updateCell((cell) => { cell.title = event.target.value; })} /></label>
              <label className="field"><span>Description</span><textarea rows={3} value={activeCell.description} onChange={(event) => updateCell((cell) => { cell.description = event.target.value; })} placeholder="What happens in this scene?" /></label>
              <NumberField label="Duration (ms)" value={activeCell.durationMs} min={250} max={120000} step={100} onChange={(value) => updateCell((cell) => { cell.durationMs = clamp(value, 250, 120000); })} />
              <button type="button" className={`secondary-button full-width ${recording ? 'recording-button' : ''}`} onClick={() => void toggleRecord()}>
                {recording ? <><Pause size={18} /> Stop recording</> : <><Mic size={18} /> Record narration</>}
              </button>
              {recordError && <p className="form-error">{recordError}</p>}
              {activeCell.audioDataUrl && (
                <div className="audio-row"><Volume2 size={18} /><audio controls src={activeCell.audioDataUrl} /><button type="button" className="icon-button danger" onClick={() => updateCell((cell) => { cell.audioDataUrl = undefined; })} aria-label="Delete narration"><Trash2 size={16} /></button></div>
              )}
            </section>

            <section className="inspector-section">
              <h3>Smart scene</h3>
              <div className="two-column-fields">
                <label className="field compact-field"><span>Time</span><select value={activeCell.scene.time} onChange={(event) => updateCell((cell) => { cell.scene.time = event.target.value as typeof activeCell.scene.time; })}><option value="day">Day</option><option value="sunset">Sunset</option><option value="night">Night</option></select></label>
                <label className="field compact-field"><span>Weather</span><select value={activeCell.scene.weather} onChange={(event) => updateCell((cell) => { cell.scene.weather = event.target.value as typeof activeCell.scene.weather; })}><option value="clear">Clear</option><option value="rain">Rain</option><option value="snow">Snow</option></select></label>
              </div>
              {activeCell.scene.weather !== 'clear' && <label className="range-field"><span>Weather intensity</span><input type="range" min={0.1} max={1} step={0.1} value={activeCell.scene.intensity} onChange={(event) => updateCell((cell) => { cell.scene.intensity = Number(event.target.value); })} /></label>}
            </section>

            <section className="inspector-section">
              <h3>Board layout</h3>
              <label className="field"><span>Cell shape</span><select value={project.layout.aspectRatio} onChange={(event) => mutate((draft) => { draft.layout.aspectRatio = event.target.value as AspectRatio; })}><option value="cinematic">Cinematic 16:9</option><option value="landscape">Classic 4:3</option><option value="square">Square 1:1</option><option value="portrait">Portrait 9:16</option></select></label>
              <NumberField label="Columns" value={project.layout.columns} min={1} max={6} onChange={(value) => mutate((draft) => { draft.layout.columns = clamp(Math.round(value), 1, 6); })} />
              <div className="switch-row"><div><strong>Show cell titles</strong><span>Include titles in board view and board exports.</span></div><Toggle checked={project.layout.showTitles} onChange={() => mutate((draft) => { draft.layout.showTitles = !draft.layout.showTitles; })} label="Show cell titles" /></div>
              <div className="switch-row"><div><strong>Show descriptions</strong><span>Add notes below each cell.</span></div><Toggle checked={project.layout.showDescriptions} onChange={() => mutate((draft) => { draft.layout.showDescriptions = !draft.layout.showDescriptions; })} label="Show cell descriptions" /></div>
            </section>

            <section className="inspector-section">
              <h3>Editing</h3>
              <div className="switch-row"><div><strong>Snap to grid</strong><span>Keep placement neat and repeatable.</span></div><Toggle checked={project.layout.snapToGrid} onChange={() => mutate((draft) => { draft.layout.snapToGrid = !draft.layout.snapToGrid; })} label="Snap to grid" /></div>
              <NumberField label="Grid size" value={project.layout.gridSize} min={1} max={200} onChange={(value) => mutate((draft) => { draft.layout.gridSize = clamp(Math.round(value), 1, 200); })} />
              <button type="button" className="revision-button" onClick={async () => {
                await saveRevision(project);
                setRevisionSaved(true);
                window.setTimeout(() => setRevisionSaved(false), 1800);
              }}>{revisionSaved ? <Check size={18} /> : <Save size={18} />} {revisionSaved ? 'Revision saved' : 'Save revision'}</button>
              <button type="button" className="text-button full-width" onClick={() => setRevisionsOpen(true)}><SquareStack size={17} /> Browse revision history</button>
            </section>
          </>
        )}

        <section className="inspector-section layers-section">
          <div className="section-title-row"><h3>Layers</h3><Layers3 size={17} /></div>
          {layers.length === 0 ? <p className="muted-copy">This cell has no editable objects yet.</p> : (
            <div className="layer-list">
              {layers.map((element) => (
                <button key={element.id} type="button" className={`layer-row ${selectedIds.includes(element.id) ? 'is-selected' : ''}`} onClick={(event) => event.shiftKey ? setSelectedIds(selectedIds.includes(element.id) ? selectedIds.filter((id) => id !== element.id) : [...selectedIds, element.id]) : setSelectedIds([element.id])}>
                  <span className={`layer-dot layer-${element.kind}`} />
                  <span className="layer-name">{element.name}</span>
                  <span className="layer-actions">
                    <span role="button" tabIndex={0} onClick={(event) => { event.stopPropagation(); updateElement(element.id, (item) => { item.hidden = !item.hidden; }); }} onKeyDown={() => undefined}>{element.hidden ? <EyeOff size={15} /> : <Eye size={15} />}</span>
                    <span role="button" tabIndex={0} onClick={(event) => { event.stopPropagation(); updateElement(element.id, (item) => { item.locked = !item.locked; }); }} onKeyDown={() => undefined}>{element.locked ? <Lock size={15} /> : <Unlock size={15} />}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </aside>
  );
};
